import React, { useMemo } from "react";
import { Reorder } from "framer-motion";

import { AnomalySummary, ChecklistItem, FVIPDOChecklistItem, FVIPipingItem, NewAnomalyItem, PDFImageProps } from "../../../../utils/models";
import { getAnomalyStartNumber, getIsSameOrAfter, getShowLastLineOption, matchPhrases } from "../../../../utils/utilities";
import { MAX_INSPECTED_ITEMS_PER_REPORT, NEW_UTM_KICKOFF_DATE } from "../../../../utils/constants";

import Conditional from "../../../common/conditional";
import CustomNewItem from "../../custom-general/custom-new-item";
import ExistingPipingItem from "../existing-piping-item";
import ItemCount from "../../../blocks/item-count";
import LineNumberForm from "../../../forms/custom-fvi/lineNumberForm";
import ReorderItem from "../../../common/reorderItem";
import WarningNotice from "../warning-notice";
import Zippy from "../../../themed/zippy";

import useIncompleteInspectedSections from "../../../../hooks/useIncompleteInspectedSections";
import useInspectedItemIDUpdate from "../../../../hooks/useInspectedItemIDUpdate";
import useProjectDetails from "../../../../hooks/useProjectDetails";

import toastService from "../../../../services/toastService";
import useAnomalyRecords from "../../../../hooks/useAnomalyRecords";

interface Props {
	checklistType?: 'piping' | 'gre';
	pipingItems: FVIPipingItem[];
    onChangePipingItems: (items: FVIPipingItem[]) => void;
}

const PipingItems: React.FC<Props> = ({ checklistType = 'piping', pipingItems, onChangePipingItems }) => {
    const { projectDetails } = useProjectDetails();
	const { incompleteSections, isLastInspectedItemComplete } = useIncompleteInspectedSections(pipingItems);

    const handleAddPipingItem = (data: Partial<FVIPipingItem>) => {
		const matchFound = pipingItems.find(({ lineNumber }) => matchPhrases(lineNumber, data.lineNumber));
		if (matchFound) return toastService.error(`${data.lineNumber} has already been added!`);

		const newPipingItem: FVIPipingItem = {
			id: pipingItems.length + 1,
			anomalyItems: [],
			anomalyLocations: [],
			anomalySummary: PIPING_ANOMALY,
			lineNumber: data.lineNumber!,
			hasPID: data.hasPID!,
			hasAnomalies: data.hasAnomalies!,
			hasPDO: data.hasPDO!,
			inspectionDescription: data.inspectionDescription!,
			pdoChecklist: PDO_CHECKLIST,
			checklistItems:  checklistType === 'piping' ? PIPING_CHECKLIST : GRE_CHECKLIST,
			checklistAdditionalComment: '',
			generalPhotos: [],
			pIDNumber: '',
			pIDImages: [],
		};

		const newPipingItems: FVIPipingItem[] = [...pipingItems, newPipingItem];
		onChangePipingItems(newPipingItems);
	};

	const handleEditPipingItem = (initialPipingItem: FVIPipingItem, updatedPipingItem: Partial<FVIPipingItem>) => {
		const matchFound = pipingItems
			.filter(({ lineNumber }) => lineNumber !== initialPipingItem.lineNumber)
			.find(({ lineNumber }) => matchPhrases(lineNumber, updatedPipingItem.lineNumber));

		if (matchFound) {
			return toastService.error(`${updatedPipingItem.lineNumber} has already been added!`);
		}

		const newPipingItems = pipingItems.map((pipingItem) =>
			pipingItem.lineNumber === initialPipingItem.lineNumber
				? {
					...pipingItem,
					lineNumber: updatedPipingItem.lineNumber!,
					pdoChecklist: updatedPipingItem.pdoChecklist || PDO_CHECKLIST,
					hasPID: updatedPipingItem.hasPID!,
					hasAnomalies: updatedPipingItem.hasAnomalies!,
					inspectionDescription: updatedPipingItem.inspectionDescription!,
					hasPDO: updatedPipingItem.hasPDO!,
				  }
				: pipingItem
		);

		onChangePipingItems(newPipingItems);
	};

	const handleChangePIDImage = (lineNumber: string, pIDData: Pick<FVIPipingItem, 'pIDImages' | 'pIDNumber'>) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						lineNumber: item.lineNumber,
						anomalyItems: item.anomalyItems,
						checklistItems: item.checklistItems,
						checklistAdditionalComment: item.checklistAdditionalComment,
						anomalyLocations: item.anomalyLocations,
						hasAnomalies: item.hasAnomalies,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						hasPID: item.hasPID,
						inspectionDescription: item.inspectionDescription,
						generalPhotos: item.generalPhotos,
						pIDImages: pIDData.pIDImages,
						pIDNumber: pIDData.pIDNumber,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleChangePDOChecklist = (lineNumber: string, items: FVIPDOChecklistItem[]) => {
		const newPipingItems = pipingItems.map((item) => item.lineNumber === lineNumber ? { ...item, pdoChecklist: items } : item);
		onChangePipingItems(newPipingItems);
	};

	const handleDeletePipingItem = (lineNumber: string): void => {
		const newPipingItems = pipingItems.filter((item) => item.lineNumber !== lineNumber);
		onChangePipingItems(newPipingItems);
	};

	const handleSetPipingChecklistItems = (lineNumber: string, items: ChecklistItem[]) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						checklistAdditionalComment: item.checklistAdditionalComment,
						lineNumber: item.lineNumber,
						hasPID: item.hasPID,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: item.anomalyItems,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						checklistItems: items,
						anomalyLocations: item.anomalyLocations,
						hasAnomalies: item.hasAnomalies,
						inspectionDescription: item.inspectionDescription,
						generalPhotos: item.generalPhotos,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleSetPipingAnomalyItems = (lineNumber: string, items: NewAnomalyItem[]) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						checklistAdditionalComment: item.checklistAdditionalComment,
						lineNumber: item.lineNumber,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: items,
						checklistItems: item.checklistItems,
						anomalyLocations: item.anomalyLocations,
						inspectionDescription: item.inspectionDescription,
						hasPID: item.hasPID,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						hasAnomalies: item.hasAnomalies,
						generalPhotos: item.generalPhotos,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleSetPipingAnomalyLocations = (lineNumber: string, images: PDFImageProps[]) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						checklistAdditionalComment: item.checklistAdditionalComment,
						lineNumber: item.lineNumber,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: item.anomalyItems,
						checklistItems: item.checklistItems,
						anomalyLocations: images,
						inspectionDescription: item.inspectionDescription,
						hasPID: item.hasPID,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						hasAnomalies: item.hasAnomalies,
						generalPhotos: item.generalPhotos,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleSetPipingGeneralPhotos = (lineNumber: string, images: PDFImageProps[]) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						checklistAdditionalComment: item.checklistAdditionalComment,
						lineNumber: item.lineNumber,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: item.anomalyItems,
						checklistItems: item.checklistItems,
						inspectionDescription: item.inspectionDescription,
						anomalyLocations: item.anomalyLocations,
						hasPID: item.hasPID,
						hasAnomalies: item.hasAnomalies,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						generalPhotos: images,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleSetAdditionalComment = (lineNumber: string, comment: string) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						anomalySummary: item.anomalySummary,
						checklistAdditionalComment: comment,
						lineNumber: item.lineNumber,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: item.anomalyItems,
						checklistItems: item.checklistItems,
						anomalyLocations: item.anomalyLocations,
						hasAnomalies: item.hasAnomalies,
						hasPID: item.hasPID,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						inspectionDescription: item.inspectionDescription,
						generalPhotos: item.generalPhotos,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

	const handleSaveAnomalySummary = (lineNumber: string, anomalySummary: AnomalySummary) => {
		const newPipingItems = pipingItems.map((item) =>
			item.lineNumber === lineNumber
				? {
						id: item.id,
						checklistAdditionalComment: item.checklistAdditionalComment,
						anomalySummary,
						lineNumber: item.lineNumber,
						pIDNumber: item.pIDNumber,
						pIDImages: item.pIDImages,
						anomalyItems: item.anomalyItems,
						checklistItems: item.checklistItems,
						anomalyLocations: item.anomalyLocations,
						hasAnomalies: item.hasAnomalies,
						hasPID: item.hasPID,
						hasPDO: item.hasPDO,
						pdoChecklist: item.pdoChecklist || PDO_CHECKLIST,
						inspectionDescription: item.inspectionDescription,
						generalPhotos: item.generalPhotos,
				  }
				: item
		);

		onChangePipingItems(newPipingItems);
	};

    const isNewFormat = useMemo(() => {
		if (!projectDetails?.createdAt) return false;

		return getIsSameOrAfter(projectDetails.createdAt, NEW_UTM_KICKOFF_DATE);
	}, [projectDetails?.createdAt]);

	useAnomalyRecords(pipingItems.flatMap((piping) => piping.anomalyItems));
	useInspectedItemIDUpdate(pipingItems.map((item) => item.lineNumber));

    return ( 
		<Zippy headerTitle="Inspected Items" isValid={pipingItems.length > 0}>
            <ItemCount count={pipingItems.length} label='Inspected Piping Items' />

			<Conditional isVisible={!isLastInspectedItemComplete}>
				<WarningNotice
					title="Piping Information Incomplete"
					body={`Please complete sections ${incompleteSections.join(', ')}`}
				/>
			</Conditional>

            <Conditional isVisible={isLastInspectedItemComplete && pipingItems.length <= MAX_INSPECTED_ITEMS_PER_REPORT}>
                <CustomNewItem label='Add New Piping Item'>
                    <LineNumberForm
                        onAdd={handleAddPipingItem} 
                        showLastLineOption={getShowLastLineOption(pipingItems, pipingItems.length - 1)} 
                    />
                </CustomNewItem>
            </Conditional>

            <Reorder.Group className="list-unstyled" values={pipingItems} onReorder={onChangePipingItems}>
                {pipingItems.map((item, index) => (
                    <ReorderItem key={item.lineNumber} value={item}>
                        <ExistingPipingItem
                            key={item.lineNumber}
                            isNewFormat={isNewFormat}
                            nextAnomalyID={getAnomalyStartNumber(pipingItems)}
                            pipingItem={item}
                            showLastLineOption={getShowLastLineOption(pipingItems, index)}
                            onChangePID={handleChangePIDImage}
                            onChangeComment={handleSetAdditionalComment}
                            onEditPipingItem={handleEditPipingItem}
                            onDeletePipingItem={handleDeletePipingItem}
                            onChangePipingChecklist={handleSetPipingChecklistItems}
                            onChangePipingAnomaly={handleSetPipingAnomalyItems}
                            onChangeGeneralPhotos={handleSetPipingGeneralPhotos}
                            onChangePipingAnomalyLocation={handleSetPipingAnomalyLocations}
							onChangePDOChecklist={handleChangePDOChecklist}
                            onSaveAnomalySummary={handleSaveAnomalySummary}
                        />
                    </ReorderItem>
                ))}
            </Reorder.Group>
        </Zippy>
	);
};

export const PDO_CHECKLIST: FVIPDOChecklistItem[] = [
	{
		id: '1',
		label: 'Are there any pre-existing dropped object hazards in the work area, such as loose items or debris?',
		value: '',
		category: 'general',
	},
	{
		id: '2',
		label: 'Are all tools properly fastened and adequately secured?',
		value: '',
		category: 'general',
	},
	{
		id: '3',
		label: 'Are equipment, objects, and tools properly secured or tied down to prevent impact or displacement due to windy or inclement weather?',
		value: '',
		category: 'general',
	},
	{
		id: '4',
		label: 'Are there any visible signs of floor gaps, damaged grating, mesh, or handrails?',
		value: '',
		category: 'general',
	},
	{
		id: '5',
		label: 'Are tools and tackles adequately secured while working at height?',
		value: '',
		category: 'general',
	},
	{
		id: '6',
		label: 'Are there any moving machinery, cranes, or vehicles in the travel path that could cause collisions or load shifts?',
		value: '',
		category: 'general',
	},
	{
		id: '7',
		label: 'Is there any poor housekeeping after completion of work?',
		value: '',
		category: 'general',
	},
	{
		id: '8',
		label: 'Is the integrity of the piping hangers satisfactory (no cracked or broken hangers, ‘bottoming out’ of spring supports, displaced support shoes, or other improper restraint conditions)?',
		value: '',
		category: 'components',
	},
	{
		id: '9',
		label: 'Are small-bore connections properly supported and free from excessive vibration or mass attached at the free end?',
		value: '',
		category: 'components',
	},
	{
		id: '10',
		label: 'Are there any improper flanges, spectacle blind, damaged temporary repairs (clamps), unauthorized modifications (flexible hoses), or valves of improper specification?',
		value: '',
		category: 'components',
	},
	{
		id: '11',
		label: 'Are dummy legs and pipe supports free from excessive corrosion and displacement?',
		value: '',
		category: 'components',
	},
];

export const GRE_CHECKLIST: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Air Bubbles',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '2',
		lineNumber: 'Blistering',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '3',
		lineNumber: 'Burn / Discolouration',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '4',
		lineNumber: 'Chalking and loose fibres',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '5',
		lineNumber: 'Chemical Attack',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '6',
		lineNumber: 'Chemical Spill',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '7',
		lineNumber: 'Chip',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '8',
		lineNumber: 'Crack',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '9',
		lineNumber: 'Crazing',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '10',
		lineNumber: 'Cut roving',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '11',
		lineNumber: 'Deformation',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '12',
		lineNumber: 'Delamination (internal)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '13',
		lineNumber: 'Dimensional changes',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '14',
		lineNumber: 'Dry Spot',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '15',
		lineNumber: 'Flange Cracks',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '16',
		lineNumber: 'Fracture',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '17',
		lineNumber: 'Pit (pin hole)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '18',
		lineNumber: 'Weld sparks',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '19',
		lineNumber: 'Tears, cuts, grinds, shoulders or any other imperfections',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '20',
		lineNumber: 'Flat thread',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '21',
		lineNumber: 'Lack of fibres',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '22',
		lineNumber: 'Misaligned joints',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '23',
		lineNumber: 'Inclusion',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '24',
		lineNumber: 'Leak',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '25',
		lineNumber: 'Support',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '26',
		lineNumber: 'Valve',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '27',
		lineNumber: 'Uneven Wall Thickness',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
];

export const PIPING_CHECKLIST: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Straight pipe runs, including where pipe rest on support',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '2',
		lineNumber: 'Branch connections, including reinforcemnt pads',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '3',
		lineNumber: 'Flanges and associated hardware',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '4',
		lineNumber: "Elbows, reducers and Tee's",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '5',
		lineNumber: 'Small piping takeoffs (Valves and piping) including Gussets',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '6',
		lineNumber: 'In-line valves (Block, Check, Control Etc)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '7',
		lineNumber: 'Dead Leg (s)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '8',
		lineNumber: 'Temporary repair clamps / Enclosures / Composite wrap',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '9',
		lineNumber: 'Filter, Stainer etc.',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '10',
		lineNumber: 'Insulation / wheather proofing / Jacketing',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-components',
	},
	{
		id: '11',
		lineNumber: 'Pipe support - Tee Slides / H" Slides / Base Supports',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '12',
		lineNumber: 'Dummy Legs',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '13',
		lineNumber: 'Anchors / Restrainsts / Guides ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '14',
		lineNumber: 'Spring Hangers / Support Rods Including Pipe Clamps',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '15',
		lineNumber: 'Thermowels and Temperature Indicating Insturments',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '16',
		lineNumber: 'Orifice Taps ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'pipe-supports',
	},
	{
		id: '17',
		lineNumber: 'Soil - to - Air Interface (Underground Lines)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '18',
		lineNumber: 'Coating Breakdown',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '19',
		lineNumber: 'Heavy Scale',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '20',
		lineNumber: 'Uncoated Surface',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '21',
		lineNumber: 'Safety valve and Associated Piping ',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '22',
		lineNumber: 'Leak',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
	{
		id: '23',
		lineNumber: 'Vibration',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'special-considerations',
	},
];

export const PIPING_ANOMALY: AnomalySummary = {
	pipeBody: '',
	noOfPoints: 0,
	pitting: '',
	noOfPittingLocations: 0,
	flanges: '',
	noOfFlanges: 0,
	fasteners: '',
	valves: '',
	noOfValves: 0,
	corrosionUnderPipeSupport: '',
	noOfCups: 0,
	compositeWrapOnLine: 'no',
	noOfWrapPoints: 0,
	conditionOfSmallBorePipings: '',
	noOfSmallBorePipings: 0,
	others: '',
	description: '',
	extentOfCoatingBreakdown: '',
};
 
export default PipingItems;